import { apiFetch } from "@/lib/apiClient";
import { QueryCache } from "@/shared/hooks/useCachedQuery";

/** One hospital a patient can connect to via Epic. `city`/`state` aren't in the
 *  backend's provider_organizations table (only id/name/fhir_base_url/environment
 *  are synced from Epic's directory), so they're intentionally not part of this
 *  shape — do not invent placeholder values for them.
 *
 *  `reachable`: whether our backend has successfully resolved this hospital's
 *  own OAuth endpoints. ~154 of 441 enabled hospitals are not reachable from
 *  the general internet at all (confirmed from three independent networks —
 *  not a bot-detection/CORS issue, a genuine TCP-level network block on
 *  those hospitals' own side) — those still appear here (never silently
 *  removed, since a hospital's reachability can change later; see the
 *  backend's oauth_endpoint_recheck.py) but reachable=false, and the picker
 *  must show them as a disabled "Coming Soon" option, not let the patient
 *  select them only to hit a dead end. */
export type EpicOrganization = {
  id: string;
  name: string;
  reachable: boolean;
};

// Same stale-while-revalidate QueryCache the admin/clinician portals already
// use (see useCachedQuery.tsx's own docstring) — a bare module-level
// singleton here rather than wired through a Context/Provider, since this
// list isn't per-user data (nothing to drop on logout, unlike the
// admin/clinician screens that cache uses). This hospital list only
// actually changes via two backend background jobs (Epic's own directory,
// re-synced once a day; OAuth-endpoint reachability, rechecked once a day
// — see directory_sync.py/oauth_endpoint_recheck.py), so refetching it
// fresh every single time the "Connect with Epic" modal opens was pure
// waste — the UI replayed the full loading skeleton every time even though
// the list is realistically unchanged from the last time the patient
// looked at it minutes or hours earlier.
const organizationsCache = new QueryCache();
const ORGANIZATIONS_CACHE_KEY = "epic-organizations";

/** The cached copy, if any — lets the picker render instantly (no loading
 *  skeleton) while listEpicOrganizations() silently revalidates in the
 *  background, exactly like useCachedQuery's own "show stale data first"
 *  behavior. */
export function getCachedEpicOrganizations(): EpicOrganization[] | undefined {
  return organizationsCache.get<EpicOrganization[]>(ORGANIZATIONS_CACHE_KEY);
}

/** GET /api/epic/organizations — the enabled hospitals a patient can pick from.
 *  Two calls made while the first is still in flight share the one request
 *  (QueryCache.fetch's own dedup) rather than firing twice. */
export async function listEpicOrganizations(): Promise<EpicOrganization[]> {
  const result = await organizationsCache.fetch<EpicOrganization[], Error>(ORGANIZATIONS_CACHE_KEY, async () => {
    const { data, error } = await apiFetch<EpicOrganization[]>("/api/epic/organizations", {
      method: "GET",
      portal: "patient",
    });
    if (error) {
      return { data: null, error: new Error(error.message || "Failed to load hospitals") };
    }
    return { data: data ?? [], error: null };
  });

  if (result.error) {
    throw result.error;
  }
  return result.data ?? [];
}

type ConnectResponse =
  | { authorize_url: string; needs_client_discovery?: false }
  | { needs_client_discovery: true; organization_id: string; smart_configuration_url: string };

/** Shape of a hospital's {fhir_base_url}/.well-known/smart-configuration
 *  document — only the two fields this flow actually needs. */
type SmartConfiguration = {
  authorization_endpoint: string;
  token_endpoint: string;
};

/** Some real hospitals' FHIR servers block server-to-server fetches of their
 *  own smart-configuration (confirmed live: Cloudflare/Incapsula/Akamai bot
 *  challenges, and plain connect-timeouts that aren't bot-detection at all —
 *  see connection_service.py's module docstring on the backend) — but the
 *  SAME fetch from the patient's own browser, on their own residential IP,
 *  goes through those same WAFs the way any normal visitor's would. This is
 *  a plain cross-origin fetch — it only succeeds if that hospital's FHIR
 *  server sends CORS headers on this specific endpoint (most real SMART-on-
 *  FHIR implementations do, since this document exists precisely so apps can
 *  fetch it), so it can still legitimately fail. */
async function discoverSmartConfigurationFromBrowser(url: string): Promise<SmartConfiguration> {
  let response: Response;
  try {
    response = await fetch(url, { headers: { Accept: "application/json" } });
  } catch {
    throw new Error(
      "Couldn't reach this hospital's system directly from your browser either. " +
      "Please try again later or contact support.",
    );
  }
  if (!response.ok) {
    throw new Error(`This hospital's system returned an error (${response.status}). Please try again later.`);
  }
  let data: Partial<SmartConfiguration>;
  try {
    data = await response.json();
  } catch {
    throw new Error("This hospital's system returned an unexpected response. Please try again later.");
  }
  if (!data.authorization_endpoint || !data.token_endpoint) {
    throw new Error("This hospital's system didn't publish the information needed to connect.");
  }
  return { authorization_endpoint: data.authorization_endpoint, token_endpoint: data.token_endpoint };
}

/** GET /auth/epic/connect — Bearer-authenticated JSON endpoint (not a raw redirect
 *  target: the patient's session lives in an in-memory token + path-scoped cookie,
 *  neither of which reach a plain browser navigation). Returns the real Epic
 *  authorize URL; the caller performs the actual `window.location.href` navigation.
 *
 *  Some hospitals' FHIR servers block our BACKEND's own discovery fetch (see
 *  connection_service.py) — when that happens, this function transparently
 *  retries the discovery from the patient's own browser instead (see
 *  discoverSmartConfigurationFromBrowser above) and reports the result to
 *  POST /connect/discovered-endpoints to finish the same connect attempt. */
export async function getEpicAuthorizeUrl(organizationId: string): Promise<string> {
  const { data, error } = await apiFetch<ConnectResponse>(
    `/auth/epic/connect?organization_id=${encodeURIComponent(organizationId)}`,
    { method: "GET", portal: "patient" },
  );

  if (error) {
    throw new Error(error.message || "Failed to start the Epic connection");
  }
  if (!data) {
    throw new Error("Epic did not return an authorization URL");
  }
  if (!data.needs_client_discovery) {
    if (!data.authorize_url) {
      throw new Error("Epic did not return an authorization URL");
    }
    return data.authorize_url;
  }

  const smartConfiguration = await discoverSmartConfigurationFromBrowser(data.smart_configuration_url);

  const retry = await apiFetch<{ authorize_url: string }>("/auth/epic/connect/discovered-endpoints", {
    method: "POST",
    portal: "patient",
    body: JSON.stringify({
      organization_id: data.organization_id,
      authorization_endpoint: smartConfiguration.authorization_endpoint,
      token_endpoint: smartConfiguration.token_endpoint,
    }),
  });

  if (retry.error) {
    throw new Error(retry.error.message || "Failed to start the Epic connection");
  }
  if (!retry.data?.authorize_url) {
    throw new Error("Epic did not return an authorization URL");
  }
  return retry.data.authorize_url;
}

export type EpicSyncResponse = {
  sync_job_id: string;
};

/** POST /api/patient/epic/sync — enqueues a pull of the connected hospital's
 *  FHIR data onto the same worker file uploads use, returning immediately
 *  with a sync_job_id rather than blocking for the whole fetch. Progress and
 *  results are then watched the same way an upload's are: poll
 *  getSyncJobStatus(sync_job_id) (syncJobApi.ts) for live per-resource-type
 *  progress, and read the stored records via getPatientRecordsDetailed with
 *  uploadId set to this same sync_job_id (FHIR rows this sync writes are
 *  tagged with it — see the backend's epic_integration/service.py). */
export async function syncEpicData(): Promise<EpicSyncResponse> {
  const { data, error } = await apiFetch<EpicSyncResponse>("/api/patient/epic/sync", {
    method: "POST",
    portal: "patient",
  });

  if (error) {
    throw new Error(error.message || "Failed to sync your Epic records");
  }
  if (!data?.sync_job_id) {
    throw new Error("Epic sync did not return a job id");
  }

  return data;
}
